import {
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  type FocusEvent as ReactFocusEvent,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  styled,
  useTheme,
  type Theme,
} from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import type {
  OrganisationId,
  OrganisationMembershipGate,
} from "@thalamos/haven-common";

import { createRoute } from "../../../routing/createRoute.js";
import {
  CARD_HEIGHT,
  CARD_WIDTH,
  type PlacedCard,
  type Standing,
  type StructureLayout,
} from "./layOutStructure.js";

/** How far a pointer moves before a press becomes a drag rather than a click. */
const DRAG_THRESHOLD = 4;
const SCALE_MIN = 0.15;
const SCALE_MAX = 2;
/** The smallest scale a drawing opens at: below it a card's words cannot be read. */
const SCALE_READABLE = 0.6;
const ZOOM_STEP = 1.25;
const VIEWPORT_HEIGHT_MAX = 560;
const VIEWPORT_HEIGHT_MIN = 240;
/** Room at the viewport's top for its buttons, so a fitted drawing clears them. */
const TOOLBAR_HEIGHT = 48;

type View = { scale: number; x: number; y: number };

/**
 * The drawing, in a viewport that can be zoomed and dragged.
 *
 * Panning and zooming write the transform straight to the canvas rather than
 * through state, so moving the drawing re-renders none of it; and every card
 * shares one class, placed by an inline position, so however many are drawn, no
 * card generates styles of its own.
 */
export const StructureDiagram = ({
  layout,
  carries,
  involvements,
  gate,
  fitKey,
  label,
  onExpand,
}: {
  layout: StructureLayout;
  /** Whether each receiving organisation receives the chosen permission. */
  carries: ReadonlyMap<OrganisationId, boolean>;
  /** The involvements that carry the chosen permission, as words. */
  involvements: string;
  gate: OrganisationMembershipGate | null;
  /** Changes whenever the drawing should open afresh. */
  fitKey: string;
  label: string;
  onExpand: (groupOf: string) => Promisable<void>;
}) => (
  <Viewport
    width={layout.width}
    height={layout.height}
    focus={layout.focus}
    fitKey={fitKey}
    label={label}
  >
    <EdgeLayer
      layout={layout}
      carries={carries}
      involvements={involvements}
      gate={gate}
    />
    <CardLayer layout={layout} gate={gate} onExpand={onExpand} />
  </Viewport>
);

const Viewport = ({
  width,
  height,
  focus,
  fitKey,
  label,
  children,
}: {
  width: number;
  height: number;
  /** Where in the drawing to centre it on opening, when the whole of it would be unreadable. */
  focus: { x: number; y: number };
  fitKey: string;
  label: string;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const viewport = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLDivElement>(null);
  const view = useRef<View>({ scale: 1, x: 0, y: 0 });
  const drawing = useRef({ width, height, focus });
  drawing.current = { width, height, focus };
  const drag = useRef<{
    startX: number;
    startY: number;
    fromX: number;
    fromY: number;
    moved: boolean;
  } | null>(null);
  const dragged = useRef(false);

  const show = useCallback((next: View) => {
    view.current = next;
    if (canvas.current !== null) {
      canvas.current.style.transform = `translate(${String(next.x)}px, ${String(next.y)}px) scale(${String(next.scale)})`;
    }
  }, []);

  /**
   * The whole drawing in view; or, opening, the focus at a readable scale
   * where the whole would be too small to read. Either way it sizes the
   * viewport to the drawing at the scale shown, between VIEWPORT_HEIGHT_MIN
   * and VIEWPORT_HEIGHT_MAX.
   */
  const fit = useCallback(
    (opening: boolean) => {
      const box = viewport.current;
      if (box === null) {
        return;
      }
      const { width: w, height: h, focus: centre } = drawing.current;
      const across = Math.min(1, box.clientWidth / w);
      const readable = opening && across < SCALE_READABLE;
      box.style.height = `${String(
        Math.min(
          VIEWPORT_HEIGHT_MAX,
          Math.max(
            VIEWPORT_HEIGHT_MIN,
            h * (readable ? SCALE_READABLE : across) + TOOLBAR_HEIGHT,
          ),
        ),
      )}px`;
      const room = box.clientHeight - TOOLBAR_HEIGHT;
      const whole = Math.max(SCALE_MIN, Math.min(across, room / h));

      show(
        opening && whole < SCALE_READABLE
          ? {
              scale: SCALE_READABLE,
              x: box.clientWidth / 2 - centre.x * SCALE_READABLE,
              // Centred on the focus, but never with the drawing's top below
              // the toolbar.
              y: Math.min(
                TOOLBAR_HEIGHT,
                TOOLBAR_HEIGHT + room / 2 - centre.y * SCALE_READABLE,
              ),
            }
          : {
              scale: whole,
              x: (box.clientWidth - w * whole) / 2,
              y: TOOLBAR_HEIGHT + Math.max(0, (room - h * whole) / 2),
            },
      );
    },
    [show],
  );

  // Opens afresh only for a different drawing: expanding a group keeps the
  // viewer where they are.
  useLayoutEffect(() => {
    fit(true);
  }, [fit, fitKey]);

  const zoom = (factor: number) => {
    const box = viewport.current;
    if (box === null) {
      return;
    }
    const { scale, x, y } = view.current;
    const next = Math.min(SCALE_MAX, Math.max(SCALE_MIN, scale * factor));
    const cx = box.clientWidth / 2;
    const cy = box.clientHeight / 2;
    show({
      scale: next,
      x: cx - ((cx - x) * next) / scale,
      y: cy - ((cy - y) * next) / scale,
    });
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }
    drag.current = {
      startX: event.clientX,
      startY: event.clientY,
      fromX: view.current.x,
      fromY: view.current.y,
      moved: false,
    };
    dragged.current = false;
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (current === null) {
      return;
    }
    const dx = event.clientX - current.startX;
    const dy = event.clientY - current.startY;
    if (!current.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) {
      return;
    }
    if (!current.moved) {
      current.moved = true;
      dragged.current = true;
      event.currentTarget.style.cursor = "grabbing";
    }
    show({ ...view.current, x: current.fromX + dx, y: current.fromY + dy });
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    drag.current = null;
    event.currentTarget.style.cursor = "";
  };

  // A card reached by keyboard is brought into view, since the viewport
  // clips whatever lies outside it.
  const onFocus = (event: ReactFocusEvent<HTMLDivElement>) => {
    const box = viewport.current;
    if (box === null) {
      return;
    }
    const outer = box.getBoundingClientRect();
    const inner = event.target.getBoundingClientRect();
    const outside =
      inner.left < outer.left ||
      inner.right > outer.right ||
      inner.top < outer.top ||
      inner.bottom > outer.bottom;
    if (outside) {
      const { x, y } = view.current;
      show({
        ...view.current,
        x: x + (outer.left + outer.width / 2 - (inner.left + inner.width / 2)),
        y: y + (outer.top + outer.height / 2 - (inner.top + inner.height / 2)),
      });
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{ position: "relative", bgcolor: "action.hover", overflow: "hidden" }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
      >
        <Button
          size="small"
          variant="outlined"
          sx={{ bgcolor: "background.paper" }}
          onClick={() => {
            zoom(ZOOM_STEP);
          }}
        >
          {t("admin.organisation.structure.zoomIn")}
        </Button>
        <Button
          size="small"
          variant="outlined"
          sx={{ bgcolor: "background.paper" }}
          onClick={() => {
            zoom(1 / ZOOM_STEP);
          }}
        >
          {t("admin.organisation.structure.zoomOut")}
        </Button>
        <Button
          size="small"
          variant="outlined"
          sx={{ bgcolor: "background.paper" }}
          onClick={() => {
            fit(false);
          }}
        >
          {t("admin.organisation.structure.fit")}
        </Button>
      </Stack>
      <Box
        ref={viewport}
        role="group"
        aria-label={label}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onFocus={onFocus}
        onClickCapture={(event) => {
          // Releasing a drag over a card is not a click on it.
          if (dragged.current) {
            event.preventDefault();
            event.stopPropagation();
            dragged.current = false;
          }
        }}
        // A drag released outside the viewport sends it no click to clear
        // `dragged`, which would then swallow the next keyboard-activated click.
        onKeyDownCapture={() => {
          dragged.current = false;
        }}
        onDragStart={(event) => {
          // A card is a link, which the browser would otherwise drag as a URL.
          event.preventDefault();
        }}
        sx={{
          position: "relative",
          // Until `fit` sizes it to the drawing.
          height: VIEWPORT_HEIGHT_MAX,
          cursor: "grab",
          touchAction: "none",
          userSelect: "none",
        }}
      >
        <Box
          ref={canvas}
          sx={{ position: "absolute", left: 0, top: 0, transformOrigin: "0 0" }}
          style={{ width, height }}
        >
          {children}
        </Box>
      </Box>
    </Paper>
  );
};

/** A label placed by inline position, so every one shares one class. */
const Caption = styled("span")(({ theme }) => ({
  ...theme.typography.caption,
  position: "absolute",
  pointerEvents: "none",
  color: theme.palette.text.secondary,
}));

const EdgeLayer = memo(function EdgeLayer({
  layout,
  carries,
  involvements,
  gate,
}: {
  layout: StructureLayout;
  carries: ReadonlyMap<OrganisationId, boolean>;
  involvements: string;
  gate: OrganisationMembershipGate | null;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const carriedColour = theme.palette.primary.main;
  const notCarriedColour = theme.palette.text.disabled;

  const cardOf = new Map<string, PlacedCard>(
    layout.cards.flatMap((card) =>
      card.kind === "organisation"
        ? [[card.organisation.id, card] as const]
        : [],
    ),
  );
  const receivers = new Set(
    layout.edges
      .filter((edge) => edge.kind === "member_of")
      .map((edge) => edge.receiverId),
  );
  const carriedLabel = t("admin.organisation.structure.edgeCarried", {
    involvements,
  });
  const notCarriedLabel =
    gate === null
      ? carriedLabel
      : t("admin.organisation.structure.edgeNotCarried", {
          setting: t(`admin.configuration.settings.keys.${gate}`),
        });

  return (
    <>
      <svg
        width={layout.width}
        height={layout.height}
        style={{ position: "absolute", inset: 0 }}
        aria-hidden
      >
        <defs>
          {[
            ["structure-carried", carriedColour],
            ["structure-not-carried", notCarriedColour],
          ].map(([id, colour]) => (
            <marker
              key={id}
              id={id}
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill={colour} />
            </marker>
          ))}
        </defs>
        {layout.edges.map((edge) => {
          const carried =
            edge.kind === "member_of" && carries.get(edge.receiverId) === true;

          return (
            <polyline
              key={edge.key}
              points={edge.points.map((point) => point.join(",")).join(" ")}
              fill="none"
              stroke={carried ? carriedColour : notCarriedColour}
              strokeWidth={carried ? 2 : 1.5}
              strokeDasharray={carried ? undefined : "5 5"}
              markerEnd={match(edge.kind)
                .with("member_of", () =>
                  carried
                    ? "url(#structure-carried)"
                    : "url(#structure-not-carried)",
                )
                .with("operated_by", () => undefined)
                .exhaustive()}
            />
          );
        })}
      </svg>

      {[...receivers].flatMap((receiverId) => {
        const card = cardOf.get(receiverId);
        if (card === undefined) {
          return [];
        }
        const carried = carries.get(receiverId) === true;
        return [
          <Caption
            key={`receives:${receiverId}`}
            aria-hidden
            style={{
              left: card.x + CARD_WIDTH / 2 + 8,
              top: card.y + CARD_HEIGHT + 6,
              whiteSpace: "nowrap",
              fontWeight: 600,
              color: carried ? carriedColour : undefined,
            }}
          >
            {carried ? carriedLabel : notCarriedLabel}
          </Caption>,
        ];
      })}

      {layout.operatingLabel === undefined ? null : (
        <Caption
          aria-hidden
          style={{
            left: layout.operatingLabel.x,
            top: layout.operatingLabel.y,
            width: layout.operatingLabel.width,
            textAlign: "center",
          }}
        >
          {t("admin.organisation.structure.operates")}
        </Caption>
      )}
    </>
  );
});

/** Every card's look, shared as one class whatever its position. */
const cardStyles = (theme: Theme) =>
  ({
    position: "absolute",
    boxSizing: "border-box",
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.25),
    padding: theme.spacing(0, 1),
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    textAlign: "center",
    textDecoration: "none",
    font: "inherit",
    cursor: "pointer",
    "&:hover": { borderColor: theme.palette.primary.main },
    "&:focus-visible": {
      borderColor: theme.palette.primary.main,
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    "& .name": {
      ...theme.typography.body2,
      fontWeight: 700,
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    // Hidden overflow would let flex squash a clamped line of text out of
    // sight; holding each at its height makes a card too short for its text
    // overflow measurably instead.
    "& > *": { flexShrink: 0 },
    "& .detail": {
      ...theme.typography.caption,
      maxWidth: "100%",
      overflow: "hidden",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
    },
    "& .secondary": { color: theme.palette.text.secondary },
  }) as const;

const OrganisationCard = styled(RouterLink)(({ theme }) => ({
  ...cardStyles(theme),
  "&[aria-current='page']": {
    border: `2px solid ${theme.palette.text.primary}`,
    background: theme.palette.action.selected,
  },
}));

const MoreCard = styled("button")(({ theme }) => ({
  ...cardStyles(theme),
  borderStyle: "dashed",
}));

const CardLayer = memo(function CardLayer({
  layout,
  gate,
  onExpand,
}: {
  layout: StructureLayout;
  gate: OrganisationMembershipGate | null;
  onExpand: (groupOf: string) => Promisable<void>;
}) {
  const { t } = useTranslation();
  const setting =
    gate === null ? null : t(`admin.configuration.settings.keys.${gate}`);
  const on = t("admin.organisation.structure.on");
  const off = t("admin.organisation.structure.off");

  return (
    <>
      {layout.cards.map((card) => {
        const position = { left: card.x, top: card.y };

        if (card.kind === "more") {
          return (
            <MoreCard
              key={card.key}
              type="button"
              style={position}
              aria-label={t("admin.organisation.structure.moreLabel", {
                count: card.count,
              })}
              onClick={() => {
                void onExpand(card.groupOf);
              }}
            >
              <span className="name">
                {t("admin.organisation.structure.more", { count: card.count })}
              </span>
            </MoreCard>
          );
        }

        const { organisation, standing } = card;
        const type = t(`organisation.type.${organisation.type}`);
        const standingText = standingOf(t, standing);
        // An aria-label replaces the card's contents as its name, so the label
        // repeats the setting shown.
        const settingText =
          gate === null || setting === null
            ? null
            : t("admin.organisation.structure.setting", {
                setting,
                state: organisation.settings[gate] ? on : off,
              });

        return (
          <OrganisationCard
            key={card.key}
            to={createRoute.adminOrganisation(organisation.id, "structure")}
            style={position}
            aria-label={
              settingText === null
                ? t("admin.organisation.structure.cardLabel", {
                    name: organisation.name,
                    type,
                    standing: standingText,
                  })
                : t("admin.organisation.structure.cardLabelWithSetting", {
                    name: organisation.name,
                    type,
                    standing: standingText,
                    setting: settingText,
                  })
            }
            aria-current={standing.kind === "focus" ? "page" : undefined}
          >
            <span className="name" title={organisation.name}>
              {organisation.name}
            </span>
            <span className="detail secondary">{type}</span>
            <span className="detail">{standingText}</span>
            {settingText === null ? null : (
              <span className="detail secondary">{settingText}</span>
            )}
          </OrganisationCard>
        );
      })}
    </>
  );
});

const standingOf = (t: TFunction, standing: Standing): string =>
  match(standing)
    .with({ kind: "focus" }, () =>
      t("admin.organisation.structure.standing.focus"),
    )
    .with({ kind: "above" }, ({ levels }) =>
      t("admin.organisation.structure.standing.above", { count: levels }),
    )
    .with({ kind: "below" }, ({ levels }) =>
      t("admin.organisation.structure.standing.below", { count: levels }),
    )
    .with({ kind: "operates" }, () =>
      t("admin.organisation.structure.standing.operates"),
    )
    .with({ kind: "operated-by" }, () =>
      t("admin.organisation.structure.standing.operated-by"),
    )
    .exhaustive();
